'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import MarketingNav from '@/app/marketing/components/MarketingNav';
import MarketingFooter from '@/app/marketing/components/MarketingFooter';
import { createClient } from '@/lib/supabase/client';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    monthly: 99,
    annual: 949,
    color: 'border-violet-500/30',
    accent: 'text-violet-400',
    bg: 'from-violet-500/5',
    btn: 'bg-violet-600 hover:bg-violet-500 text-white',
    features: [
      '500 inventory items',
      '1 user (Owner role)',
      'Rapaport calculator',
      'QR scanning',
      'Basic P&L',
      'Email support',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    monthly: 249,
    annual: 2390,
    color: 'border-amber-500/50',
    accent: 'text-amber-400',
    bg: 'from-amber-500/8',
    btn: 'bg-amber-500 hover:bg-amber-400 text-[#0a0e1a]',
    highlight: true,
    features: [
      '5,000 inventory items',
      'Up to 3 users',
      'Full CRM + pipeline',
      'Email automation',
      'Advanced analytics',
      'Priority support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthly: 599,
    annual: 5750,
    color: 'border-indigo-500/30',
    accent: 'text-indigo-400',
    bg: 'from-indigo-500/5',
    btn: 'bg-indigo-600 hover:bg-indigo-500 text-white',
    features: [
      'Unlimited inventory',
      'Up to 10 users',
      'Multi-location',
      'API access',
      'Dedicated manager',
      'SLA guarantee',
    ],
  },
];

interface FormState {
  name: string;
  email: string;
  phone: string;
  company: string;
}

export default function SignupPage() {
  const supabase = createClient();
  const [step, setStep] = useState<'plans' | 'form' | 'success'>('plans');
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<(typeof PLANS)[0] | null>(null);
  const [form, setForm] = useState<FormState>({ name: '', email: '', phone: '', company: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSelectPlan = (plan: (typeof PLANS)[0]) => {
    setSelectedPlan(plan);
    setStep('form');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !selectedPlan) return;
    setLoading(true);
    setError('');

    try {
      const now = new Date();
      const nextBilling = new Date(now);
      if (billing === 'monthly') {
        nextBilling.setMonth(nextBilling.getMonth() + 1);
      } else {
        nextBilling.setFullYear(nextBilling.getFullYear() + 1);
      }

      const amountPaid = billing === 'monthly' ? selectedPlan.monthly : selectedPlan.annual;

      // 1. Insert into active_users
      const { data: userData, error: userError } = await supabase
        .from('active_users')
        .insert({
          full_name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          company_name: form.company || null,
          phone: form.phone || null,
          subscription_plan: selectedPlan.name,
          subscription_status: 'Active',
          billing_cycle: billing === 'monthly' ? 'Monthly' : 'Annual',
          subscription_start_date: now.toISOString(),
          next_billing_date: nextBilling.toISOString(),
          amount_paid: amountPaid,
          mrr: billing === 'monthly' ? amountPaid : Math.round(amountPaid / 12),
          feature_highlights: selectedPlan.features,
        })
        .select('id')
        .single();

      if (userError && !userError.message.includes('duplicate')) {
        throw new Error(userError.message);
      }

      const activeUserId = userData?.id;

      // 2. Send confirmation email
      const emailRes = await fetch(
        `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/send-registration-emails`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            emailType: 'real_customer_signup',
            customerName: form.name.trim(),
            customerEmail: form.email.trim().toLowerCase(),
            planName: selectedPlan.name,
            billingCycle: billing === 'monthly' ? 'Monthly' : 'Annual',
            subscriptionStartDate: now.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
            nextBillingDate: nextBilling.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
            amountPaid: amountPaid.toFixed(2),
          }),
        }
      );

      // 3. Log the email
      if (activeUserId) {
        await supabase.from('email_logs').insert({
          email_type: 'real_customer_signup_confirmation',
          recipient_email: form.email.trim().toLowerCase(),
          recipient_name: form.name.trim(),
          subject: 'Welcome Aboard! Your Glanz CRM Account is Active — Login Details Inside',
          status: emailRes.ok ? 'sent' : 'failed',
          reference_id: activeUserId,
          reference_table: 'active_users',
        });
      }

      setStep('success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0a0e1a] text-[#e5e7eb]"
      style={{ fontFamily: 'var(--font-plus-jakarta-sans), system-ui, sans-serif' }}
    >
      <MarketingNav />
      <main id="main-content">
        <section className="pt-28 pb-20 px-4 sm:px-6 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-gradient-to-b from-emerald-500/5 via-violet-600/5 to-transparent blur-[120px]" />
          </div>

          <div className="max-w-5xl mx-auto relative z-10">
            {/* ── Plans Step ── */}
            {step === 'plans' && (
              <>
                <div className="text-center mb-12">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/8 text-emerald-300 text-xs font-medium mb-5">
                    Get Started Today
                  </div>
                  <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
                    Choose Your Plan
                  </h1>
                  <p className="text-[#9ca3af] text-lg mb-8">
                    Full access immediately after signup. Cancel anytime.
                  </p>

                  {/* Billing toggle */}
                  <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-[#111827] border border-white/8">
                    <button
                      onClick={() => setBilling('monthly')}
                      className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${billing === 'monthly' ? 'bg-white/10 text-white' : 'text-[#9ca3af] hover:text-white'}`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setBilling('annual')}
                      className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${billing === 'annual' ? 'bg-white/10 text-white' : 'text-[#9ca3af] hover:text-white'}`}
                    >
                      Annual
                      <span className="text-xs bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full font-semibold">
                        Save 20%
                      </span>
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  {PLANS.map((plan) => (
                    <div
                      key={plan.id}
                      className={`relative rounded-2xl border p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 bg-gradient-to-b ${plan.bg} to-[#111827] ${plan.color} ${plan.highlight ? 'shadow-xl shadow-amber-500/10' : ''}`}
                    >
                      {plan.highlight && (
                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                          <span className="px-4 py-1.5 rounded-full bg-amber-500 text-[#0a0e1a] text-xs font-bold shadow-lg">
                            ★ Most Popular
                          </span>
                        </div>
                      )}
                      <div
                        className={`text-xs font-bold uppercase tracking-widest ${plan.accent} mb-2`}
                      >
                        {plan.name}
                      </div>
                      <div className="mb-6">
                        <span className="text-4xl font-black text-white">
                          ${billing === 'monthly' ? plan.monthly : plan.annual}
                        </span>
                        <span className="text-[#9ca3af] text-sm ml-1">
                          {billing === 'monthly' ? '/month' : '/year'}
                        </span>
                      </div>
                      <ul className="space-y-2.5 mb-8 flex-1">
                        {plan.features.map((feat) => (
                          <li
                            key={feat}
                            className="flex items-start gap-2.5 text-sm text-[#d1d5db]"
                          >
                            <svg
                              className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2.5}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            {feat}
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => handleSelectPlan(plan)}
                        className={`w-full py-3 rounded-xl text-sm font-bold text-center transition-colors ${plan.btn}`}
                      >
                        Get Started with {plan.name} →
                      </button>
                    </div>
                  ))}
                </div>

                <p className="text-center text-sm text-[#9ca3af] mt-8">
                  Want to try first?{' '}
                  <Link
                    href="/demo"
                    className="text-amber-400 hover:text-amber-300 transition-colors"
                  >
                    Start a free 14-day demo
                  </Link>
                </p>
              </>
            )}

            {/* ── Form Step ── */}
            {step === 'form' && selectedPlan && (
              <div className="max-w-lg mx-auto">
                <button
                  onClick={() => setStep('plans')}
                  className="flex items-center gap-1.5 text-sm text-[#9ca3af] hover:text-white transition-colors mb-6"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Back to plans
                </button>

                {/* Selected plan summary */}
                <div
                  className={`rounded-xl border ${selectedPlan.color} bg-gradient-to-r ${selectedPlan.bg} to-transparent p-4 mb-6 flex items-center justify-between`}
                >
                  <div>
                    <p
                      className={`text-xs font-bold uppercase tracking-widest ${selectedPlan.accent}`}
                    >
                      {selectedPlan.name} Plan
                    </p>
                    <p className="text-white font-bold text-lg mt-0.5">
                      ${billing === 'monthly' ? selectedPlan.monthly : selectedPlan.annual}
                      <span className="text-[#9ca3af] text-sm font-normal ml-1">
                        {billing === 'monthly' ? '/month' : '/year'}
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={() => setStep('plans')}
                    className="text-xs text-[#9ca3af] hover:text-white border border-white/10 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    Change
                  </button>
                </div>

                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-white mb-2">Create Your Account</h2>
                  <p className="text-[#9ca3af] text-sm">
                    Your account will be activated immediately. Login details sent by email.
                  </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-[#111827] p-8">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border border-white/10 text-sm text-white placeholder-[#8b93a3] focus:outline-none focus:border-emerald-500/50 transition-colors"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                        Work Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border border-white/10 text-sm text-white placeholder-[#8b93a3] focus:outline-none focus:border-emerald-500/50 transition-colors"
                        placeholder="you@company.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={form.company}
                        onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border border-white/10 text-sm text-white placeholder-[#8b93a3] focus:outline-none focus:border-emerald-500/50 transition-colors"
                        placeholder="Diamond Co."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                        Phone
                      </label>
                      <input
                        type="text"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border border-white/10 text-sm text-white placeholder-[#8b93a3] focus:outline-none focus:border-emerald-500/50 transition-colors"
                        placeholder="+1 555 000 0000"
                      />
                    </div>

                    {error && (
                      <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                        {error}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={loading || !form.name.trim() || !form.email.trim()}
                      className="w-full py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-bold text-sm shadow-xl shadow-emerald-500/20 transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Activating your account...
                        </span>
                      ) : (
                        `Activate ${selectedPlan.name} Plan →`
                      )}
                    </button>

                    <p className="text-xs text-center text-[#8b93a3]">
                      Login details will be emailed immediately after signup.
                    </p>
                  </form>
                </div>
              </div>
            )}

            {/* ── Success Step ── */}
            {step === 'success' && selectedPlan && (
              <div className="max-w-lg mx-auto text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
                  <svg
                    className="w-10 h-10 text-emerald-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-white mb-3">Account Activated!</h2>
                <p className="text-[#9ca3af] text-lg mb-2">
                  Check your inbox at <strong className="text-white">{form.email}</strong>
                </p>
                <p className="text-[#9ca3af] text-sm mb-8">
                  Your <strong className={selectedPlan.accent}>{selectedPlan.name} Plan</strong> is
                  now active. We've sent your login details and subscription summary.
                </p>

                <div className="rounded-2xl border border-white/8 bg-[#111827] p-6 mb-8 text-left">
                  <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wide mb-4">
                    What's in your email
                  </p>
                  <div className="space-y-3">
                    {[
                      { icon: '🔑', text: 'Your login URL and credentials' },
                      { icon: '📋', text: `${selectedPlan.name} plan details and features` },
                      { icon: '💳', text: 'Billing cycle and next payment date' },
                      { icon: '📞', text: 'Support contact and onboarding guide' },
                    ].map((item) => (
                      <div key={item.text} className="flex items-center gap-3">
                        <span className="text-lg">{item.icon}</span>
                        <span className="text-sm text-[#d1d5db]">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link
                  href="/login"
                  className="inline-block px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold text-sm hover:from-emerald-400 hover:to-emerald-500 transition-all"
                >
                  Login to Your Account →
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
